import React, { useEffect, useState } from "react";
import { Wallet as WalletIcon, Plus, ArrowUp, ArrowDown, WhatsappLogo, QrCode, Megaphone, ChatDots, DownloadSimple, ArrowUUpLeft } from "@phosphor-icons/react";
import { PageLoader, Stat } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { toast } from "sonner";
import api, { API, TOKEN_KEY, money, apiErr } from "../../lib/api";
import { loadRazorpay } from "../../lib/razorpay";
import { useAuth } from "../../context/AuthContext";

const QUICK = [500, 1000, 2000, 5000];
const PRICE_META = {
  whatsapp_message: { label: "WhatsApp message", icon: WhatsappLogo, unit: "per message" },
  sms: { label: "SMS", icon: ChatDots, unit: "per message" },
  qr_ticket: { label: "QR ticket", icon: QrCode, unit: "per pass" },
  marketing_campaign: { label: "Marketing", icon: Megaphone, unit: "per campaign" },
};

export default function Wallet() {
  const { user, refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [refunds, setRefunds] = useState([]);
  const [amount, setAmount] = useState(1000);
  const [busy, setBusy] = useState(false);
  const [refund, setRefund] = useState({ amount: "", payment_details: "" });

  const load = () => Promise.all([
    api.get("/organizer/wallet"), api.get("/organizer/wallet/analytics"), api.get("/organizer/wallet/refund-requests"),
  ]).then(([w, a, r]) => { setData(w.data); setAnalytics(a.data); setRefunds(r.data.requests); });
  useEffect(() => { load(); }, []);

  const recharge = async () => {
    const amt = Number(amount);
    if (!amt || amt < 100) { toast.error("Minimum recharge is ₹100."); return; }
    setBusy(true);
    try {
      const { data: order } = await api.post("/organizer/wallet/recharge/order", { amount: amt });
      if (!order.enabled) { toast.success(order.message || "Wallet recharged."); await load(); await refreshUser(); setBusy(false); return; }
      await loadRazorpay();
      const rzp = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: order.currency, order_id: order.order_id,
        name: "EventVexa · Services Wallet", description: `Add ${money(amt)} to wallet`,
        prefill: { name: user?.name || "", email: user?.email || "", contact: user?.mobile || "" },
        theme: { color: "#7C3AED" },
        handler: async (r) => {
          try { const { data: res } = await api.post("/organizer/wallet/recharge/verify", {
            razorpay_order_id: r.razorpay_order_id, razorpay_payment_id: r.razorpay_payment_id, razorpay_signature: r.razorpay_signature });
            toast.success(res.message); await load(); await refreshUser();
          } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
        },
        modal: { ondismiss: () => { setBusy(false); toast.info("Recharge cancelled."); } },
      });
      rzp.on("payment.failed", (resp) => { toast.error(resp?.error?.description || "Payment failed."); setBusy(false); });
      rzp.open();
    } catch (e) { toast.error(apiErr(e)); setBusy(false); }
  };

  const exportTx = async (fmt) => {
    try {
      const res = await fetch(`${API}/organizer/wallet/export?format=${fmt}`, { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a"); a.href = url; a.download = `eventra-wallet.${fmt}`; a.click(); URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()}.`);
    } catch { toast.error("Could not export."); }
  };

  const requestRefund = async () => {
    try {
      const { data: res } = await api.post("/organizer/wallet/refund-request", { amount: Number(refund.amount), payment_details: refund.payment_details });
      toast.success(res.message); setRefund({ amount: "", payment_details: "" }); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!data || !analytics) return <PageLoader />;
  const sym = data.currency_symbol || "₹";

  return (
    <div className="max-w-4xl">
      <div className="overline mb-1">Prepaid service spending</div>
      <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mb-6">Services Wallet</h1>

      <Tabs defaultValue="overview">
        <TabsList className="mb-6">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="usage" data-testid="tab-usage">Usage & Spend</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="refund" data-testid="tab-refund">Refund</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-5">
            <div className="rounded-xl border border-transparent brand-gradient text-white p-6 shadow-lg shadow-primary/20" data-testid="wallet-balance-card">
              <div className="flex items-center gap-2 text-white/70 text-sm"><WalletIcon size={18} weight="fill" /> Current balance</div>
              <div className="font-heading text-4xl font-bold tracking-tight mt-2" data-testid="wallet-balance">{money(data.balance, sym)}</div>
              <div className="flex gap-4 mt-3 text-xs text-white/60">
                <span>Spent {money(analytics.total_spent, sym)}</span><span>Added {money(analytics.total_added, sym)}</span>
              </div>
            </div>
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="font-semibold mb-3">Recharge wallet</div>
              <div className="flex flex-wrap gap-2 mb-3">
                {QUICK.map((q) => (
                  <button key={q} onClick={() => setAmount(q)} data-testid={`quick-${q}`}
                    className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-colors ${Number(amount) === q ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-foreground/30"}`}>{money(q, sym)}</button>
                ))}
              </div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">{sym}</span>
                  <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="pl-7 h-11" data-testid="recharge-amount" />
                </div>
                <Button onClick={recharge} disabled={busy} className="rounded-full h-11 px-5" data-testid="recharge-btn"><Plus className="mr-1" /> {busy ? "…" : "Add money"}</Button>
              </div>
            </div>
          </div>
          <div>
            <div className="overline mb-3">Current per-action rates (set by admin)</div>
            <div className="grid sm:grid-cols-4 gap-4">
              {Object.entries(data.pricing || {}).map(([k, v]) => {
                const m = PRICE_META[k] || { label: k, icon: Megaphone, unit: "" }; const Icon = m.icon;
                return (
                  <div key={k} className="rounded-xl border border-border bg-card p-4" data-testid={`price-${k}`}>
                    <Icon size={20} className="text-primary" weight="fill" />
                    <div className="font-heading font-semibold mt-2">{money(v, sym)}</div>
                    <div className="text-xs text-muted-foreground">{m.label} · {m.unit}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="usage" className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Total spent" value={money(analytics.total_spent, sym)} accent />
            <Stat label="Messages sent" value={analytics.message_count} />
            <Stat label="QR passes" value={analytics.qr_count} />
            <Stat label="Balance" value={money(data.balance, sym)} />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="overline mb-3">Spend by service</div>
              {analytics.by_service.length === 0 ? <p className="text-sm text-muted-foreground">No spending yet.</p> : (
                <div className="rounded-xl border border-border bg-card divide-y divide-border">
                  {analytics.by_service.map((s) => (
                    <div key={s.service} className="flex justify-between p-3 text-sm" data-testid={`spend-svc-${s.service}`}>
                      <span>{(PRICE_META[s.service] || {}).label || s.service} <span className="text-muted-foreground">· {s.count}</span></span>
                      <span className="font-semibold">{money(s.amount, sym)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="overline mb-3">Spend by event</div>
              {analytics.by_event.length === 0 ? <p className="text-sm text-muted-foreground">No event spending yet.</p> : (
                <div className="rounded-xl border border-border bg-card divide-y divide-border">
                  {analytics.by_event.map((s) => (
                    <div key={s.event_id} className="flex justify-between p-3 text-sm">
                      <span className="truncate max-w-[70%]">{s.event}</span><span className="font-semibold">{money(s.amount, sym)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="transactions" className="space-y-4">
          <div className="flex justify-end gap-2">
            {["csv", "xlsx", "pdf"].map((f) => (
              <Button key={f} size="sm" variant="outline" onClick={() => exportTx(f)} className="rounded-full" data-testid={`wallet-export-${f}`}>
                <DownloadSimple size={14} className="mr-1" /> {f.toUpperCase()}
              </Button>
            ))}
          </div>
          {(data.transactions || []).length === 0 ? <p className="text-sm text-muted-foreground">No transactions yet.</p> : (
            <div className="rounded-xl border border-border bg-card divide-y divide-border" data-testid="wallet-transactions">
              {data.transactions.map((t) => (
                <div key={t.id} className="flex items-center justify-between p-3.5" data-testid={`txn-${t.id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`w-8 h-8 grid place-items-center rounded-full ${t.amount >= 0 ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>
                      {t.amount >= 0 ? <ArrowDown size={15} /> : <ArrowUp size={15} />}</span>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{t.reason}</div>
                      <div className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString("en-IN")}{(t.meta || {}).recipient_name ? ` · ${t.meta.recipient_name}` : ""}</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`font-semibold ${t.amount >= 0 ? "text-emerald-400" : "text-foreground"}`}>{t.amount >= 0 ? "+" : ""}{money(t.amount, sym)}</div>
                    <div className="text-xs text-muted-foreground">Bal {money(t.balance_after, sym)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="refund" className="space-y-5 max-w-lg">
          <p className="text-sm text-muted-foreground">Request a payout of unused wallet balance. This is separate from ticket-sales settlements. Admin reviews and transfers manually.</p>
          <div className="rounded-xl border border-border bg-card p-5 space-y-3">
            <div><label className="text-sm font-medium mb-1 block">Amount ({sym})</label>
              <Input type="number" value={refund.amount} onChange={(e) => setRefund({ ...refund, amount: e.target.value })} placeholder="Min ₹100" data-testid="refund-amount" /></div>
            <div><label className="text-sm font-medium mb-1 block">Bank / UPI details</label>
              <Textarea value={refund.payment_details} onChange={(e) => setRefund({ ...refund, payment_details: e.target.value })} rows={2} placeholder="Account no + IFSC, or UPI ID" data-testid="refund-details" /></div>
            <Button onClick={requestRefund} className="rounded-full" data-testid="submit-refund"><ArrowUUpLeft size={15} className="mr-1.5" /> Request refund</Button>
          </div>
          {refunds.length > 0 && (
            <div className="rounded-xl border border-border bg-card divide-y divide-border">
              {refunds.map((r) => (
                <div key={r.id} className="flex justify-between p-3.5 text-sm" data-testid={`refund-${r.id}`}>
                  <div><div className="font-medium">{money(r.amount, sym)}</div><div className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString("en-IN")}</div></div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-muted capitalize h-fit">{r.status}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
